/**
 * useAdminUsers Hook
 * Provides access to admin user data with CRUD operations
 * Admins are users with admin role (different from superusers)
 */

import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { adminsQueryAtom, allUsersQueryAtom } from "@/store/adminDataStore";
import { adminAPI } from "@/services";
import type { IUser } from "@/services";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UseAdminUsersReturn {
  /** All admin emails */
  admins: string[];
  /** All users (for autocomplete) */
  allUsers: IUser[];
  /** Users that are not currently admins (for add suggestions) */
  nonAdminUsers: IUser[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Add a user as admin */
  addAdmin: (email: string) => Promise<boolean>;
  /** Remove admin role from user */
  removeAdmin: (email: string) => Promise<boolean>;
  /** Check if email is an admin */
  isAdmin: (email: string) => boolean;
  /** Adding state */
  isAdding: boolean;
  /** Add error */
  addError: Error | null;
  /** Removing state */
  isRemoving: boolean;
  /** Remove error */
  removeError: Error | null;
}

export function useAdminUsers(): UseAdminUsersReturn {
  const [adminsQuery] = useAtom(adminsQueryAtom);
  const [allUsersQuery] = useAtom(allUsersQueryAtom);
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<Error | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<Error | null>(null);

  // Admin emails
  const admins = useMemo(() => adminsQuery.data ?? [], [adminsQuery.data]);

  // All users
  const allUsers = useMemo(
    () => allUsersQuery.data ?? [],
    [allUsersQuery.data],
  );

  // Set of normalized admin emails for quick lookup
  const adminEmailsSet = useMemo(() => {
    const set = new Set<string>();
    admins.forEach((email) => set.add(email.toLowerCase().trim()));
    return set;
  }, [admins]);

  // Non-admin users (for suggestions)
  const nonAdminUsers = useMemo(
    () =>
      allUsers.filter(
        (user) => !adminEmailsSet.has(user.email.toLowerCase().trim()),
      ),
    [allUsers, adminEmailsSet],
  );

  // Check if email is admin
  const isAdmin = useCallback(
    (email: string) => adminEmailsSet.has(email.toLowerCase().trim()),
    [adminEmailsSet],
  );

  // Add admin mutation
  const addAdmin = useCallback(
    async (email: string): Promise<boolean> => {
      setIsAdding(true);
      setAddError(null);

      try {
        await adminAPI.addAdmin(email);
        await invalidateWithTimeout(queryClient, ["admins"]);
        return true;
      } catch (error) {
        setAddError(error as Error);
        return false;
      } finally {
        setIsAdding(false);
      }
    },
    [queryClient],
  );

  // Remove admin mutation
  const removeAdmin = useCallback(
    async (email: string): Promise<boolean> => {
      setIsRemoving(true);
      setRemoveError(null);

      try {
        await adminAPI.deleteAdmin(email);
        await invalidateWithTimeout(queryClient, ["admins"]);
        return true;
      } catch (error) {
        setRemoveError(error as Error);
        return false;
      } finally {
        setIsRemoving(false);
      }
    },
    [queryClient],
  );

  // Invalidate queries - marks as stale and refetches active queries
  const refetch = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["admins"],
        refetchType: "active",
      }),
      queryClient.invalidateQueries({
        queryKey: ["allUsers"],
        refetchType: "active",
      }),
    ]);
  }, [queryClient]);

  return {
    admins,
    allUsers,
    nonAdminUsers,
    isLoading: adminsQuery.isLoading || allUsersQuery.isLoading,
    error: adminsQuery.error ?? allUsersQuery.error ?? null,
    refetch,
    addAdmin,
    removeAdmin,
    isAdmin,
    isAdding,
    addError,
    isRemoving,
    removeError,
  };
}
